/* eslint-disable complexity */
import { css } from "@compiled/react";

import { AddUser } from "./icons/avatar/add-user";
import { LongHair } from "./icons/avatar/long-hair";
import { CaretIcon } from "./icons/basic/caret";
import { Checkmark as CheckmarkBasic } from "./icons/basic/checkmark";
import { CheckmarkCircle } from "./icons/basic/checkmark-circle";
import { Cross } from "./icons/basic/cross";
import { FeatureCheckmark } from "./icons/basic/feature-checkmark";
import { InfoCircle } from "./icons/basic/info-circle";
import { No } from "./icons/basic/no";
import { ClosedLock } from "./icons/closed-lock";
import { Accelerator } from "./icons/content/accelerator";
import { Checkmark } from "./icons/content/checkmark";
import { DiagramOrgChart } from "./icons/content/diagram-org-chart";
import { Lightning } from "./icons/content/lightning";
import { Maximize } from "./icons/content/maximize";
import { PageMultiple } from "./icons/content/page-multiple";
import { Partners } from "./icons/content/partners";
import { Sparkle } from "./icons/content/sparkle";
import { Team } from "./icons/content/team";
import { Bold } from "./icons/object/bold";
import { Building } from "./icons/object/building";
import { CircleUnions } from "./icons/object/circle-unions";
import { Cog } from "./icons/object/cog";
import { FourSquares } from "./icons/object/four-squares";
import { Globe } from "./icons/object/globe";
import { Id } from "./icons/object/id";
import { Lightbulb } from "./icons/object/lightbulb";
import { LocationPin } from "./icons/object/location-pin";
import { Lock } from "./icons/object/lock";
import { OpenLock } from "./icons/object/open-lock";
import { PuzzlePiece } from "./icons/object/puzzle-piece";
import { SecurityCheckmark } from "./icons/object/security-checkmark";
import { Video } from "./icons/object/video";
import { Workflow } from "./icons/object/workflow";
import { Wrench } from "./icons/object/wrench";
import { CloudCheckmark } from "./icons/software/cloud-checkmark";
import { DataCenter } from "./icons/software/data-center";

const IconNames = [
    "adduser",
    "longhair",
    "caret",
    "checkmarkbasic",
    "checkmark",
    "checkmarkcircle",
    "featurecheckmark",
    "circleunions",
    "cross",
    "no",
    "diagramorgchart",
    "maximize",
    "pagemultiple",
    "bold",
    "building",
    "puzzlepiece",
    "securitycheckmark",
    "lightbulb",
    "locationpin",
    "lock",
    "video",
    "wrench",
    "cloudcheckmark",
    "datacenter",
    "accelerator",
    "team",
    "partners",
    "sparkle",
    "lightning",
    "id",
    "info",
    "infocircle",
    "globe",
    "workflow",
    "foursquares",
    "openlock",
    "closedlock",
    "cog"
] as const;

export type IconNameTypes = (typeof IconNames)[number];

export interface IconProps {
    name: IconNameTypes;
    size?: "xs" | "small" | "medium" | "large";
    color?: string;
}

const IconSize = {
    xs: "24px",
    small: "32px",
    medium: "48px",
    large: "64px",
    default: "48px"
} as const;

type IconSizeType = keyof typeof IconSize;

const basicStyle = css({
    lineHeight: 0
});

export const IconFetcher = (
    name: IconNameTypes,
    size: IconSizeType = "default"
) => {
    const iconSize = IconSize[size];
    switch (name) {
        // avatar
        case "adduser":
            return <AddUser size={iconSize} />;
        case "longhair":
            return <LongHair size={iconSize} />;

        // basic
        case "caret":
            return <CaretIcon />;
        case "checkmarkbasic":
            return <CheckmarkBasic size={iconSize} />;
        case "checkmarkcircle":
            return <CheckmarkCircle size={iconSize} />;
        case "featurecheckmark":
            return <FeatureCheckmark size={iconSize} />;
        case "cross":
            return <Cross size={iconSize} />;
        case "no":
            return <No size={iconSize} />;
        case "infocircle":
            return <InfoCircle size={iconSize} />;

        // content
        case "checkmark":
            return <Checkmark size={iconSize} />;
        case "diagramorgchart":
            return <DiagramOrgChart size={iconSize} />;
        case "maximize":
            return <Maximize size={iconSize} />;
        case "pagemultiple":
            return <PageMultiple size={iconSize} />;

        // object
        case "bold":
            return <Bold size={iconSize} />;
        case "building":
            return <Building size={iconSize} />;
        case "circleunions":
            return <CircleUnions size={iconSize} />;
        case "id":
            return <Id size={iconSize} />;
        case "puzzlepiece":
            return <PuzzlePiece size={iconSize} />;
        case "securitycheckmark":
            return <SecurityCheckmark size={iconSize} />;
        case "lightbulb":
            return <Lightbulb size={iconSize} />;
        case "locationpin":
            return <LocationPin size={iconSize} />;
        case "lock":
            return <Lock size={iconSize} />;
        case "video":
            return <Video size={iconSize} />;
        case "wrench":
            return <Wrench size={iconSize} />;
        case "accelerator":
            return <Accelerator size={iconSize} />;
        case "team":
            return <Team size={iconSize} />;
        case "partners":
            return <Partners size={iconSize} />;
        case "sparkle":
            return <Sparkle size={iconSize} />;
        case "lightning":
            return <Lightning size={iconSize} />;
        case "globe":
            return <Globe size={iconSize} />;
        case "workflow":
            return <Workflow size={iconSize} />;
        case "foursquares":
            return <FourSquares size={iconSize} />;
        case "openlock":
            return <OpenLock size={iconSize} />;
        case "closedlock":
            return <ClosedLock size={iconSize} />;
        case "cog":
            return <Cog size={iconSize} />;

        // software
        case "cloudcheckmark":
            return <CloudCheckmark size={iconSize} />;
        case "datacenter":
            return <DataCenter size={iconSize} />;

        default:
            return null;
    }
};

export const Icon = ({ name, size, color }: IconProps) => {
    const iconColor = color;
    return (
        <div
            className="icon"
            css={basicStyle}
            style={{ color: iconColor ? iconColor : "inherit" }}
        >
            {IconFetcher(name, size)}
        </div>
    );
};
